import { createRouter, createWebHistory } from 'vue-router'

import { hasPermission, permissionNames } from '../auth/authorization'
import { registerAuthGuard } from '../auth/guard'
import eventActivities from '../pages/events/Activities.vue'
import eventCreate from '../pages/events/CreateEvent.vue'
import eventDocuments from '../pages/events/Documents.vue'
import eventDetail from '../pages/events/EventDetail.vue'
import eventEdit from '../pages/events/EditEvent.vue'
import eventInviteeDetail from '../pages/events/InviteeDetail.vue'
import eventInviteeActivityTracking from '../pages/events/InviteeActivityTracking.vue'
import eventInviteeDocumentSign from '../pages/events/InviteeDocumentSigning.vue'
import eventManageInvitee from '../pages/events/ManageInvitee.vue'
import eventInvitees from '../pages/events/Invitees.vue'
import eventVehicleSelection from '../pages/events/SelectVehicles.vue'
import eventVehicles from '../pages/events/Vehicles.vue'
import eventsIndex from '../pages/events/Index.vue'
import fleetCalendar from '../pages/fleet/Calendar.vue'
import fleetRecordForm from '../pages/fleet/RecordForm.vue'
import fleetRecordView from '../pages/fleet/RecordView.vue'
import fleetPickupRecordTerms from '../pages/fleet/PickupRecordTerms.vue'
import fleetRecordsList from '../pages/fleet/RecordsList.vue'
import fleetReservationCreate from '../pages/fleet/CreateReservation.vue'
import inviteesIndex from '../pages/invitees/Index.vue'
import inventoryVehicleDetail from '../pages/inventory/VehicleDetail.vue'
import inventoryVehicleManage from '../pages/inventory/ManageVehicle.vue'
import inventoryVehicles from '../pages/inventory/Index.vue'
import forbidden from '../pages/Forbidden.vue'

const routes = [
  {
    path: '/',
    redirect: () => {
      if (hasPermission(permissionNames.eventsAccess)) {
        return { name: 'events-list' }
      }

      if (hasPermission(permissionNames.driveparkAccess)) {
        return { name: 'fleet-calendar' }
      }

      return { name: 'forbidden' }
    },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: forbidden,
    meta: {
      title: 'Pristup nije dozvoljen',
    },
  },
  {
    path: '/events',
    name: 'events-list',
    component: eventsIndex,
    meta: {
      title: 'Popis događaja',
      requiredPermissions: [permissionNames.eventsAccess],
    },
  },
  {
    path: '/events/invitees',
    name: 'invitees-list',
    component: inviteesIndex,
    meta: {
      title: 'Popis uzvanika',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsCandidatesAccess,
      ],
    },
  },
  {
    path: '/invitees',
    redirect: { name: 'invitees-list' },
  },
  {
    path: '/events/documents',
    name: 'event-documents',
    component: eventDocuments,
    meta: {
      title: 'Popis dokumenata',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsEdit,
      ],
    },
  },
  {
    path: '/events/documents/:documentId',
    name: 'event-document-detail',
    component: eventInviteeDocumentSign,
    meta: {
      bodyOnly: true,
      title: 'Pregled dokumenta',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsEdit,
      ],
      breadcrumb: { label: 'Pregled dokumenta' },
    },
  },
  {
    path: '/events/activities',
    name: 'event-activities',
    component: eventActivities,
    meta: {
      title: 'Popis aktivnosti',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsEdit,
      ],
    },
  },
  {
    path: '/fleet/calendar',
    name: 'fleet-calendar',
    component: fleetCalendar,
    meta: {
      title: 'Kalendar zauzetosti',
      requiredPermissions: [permissionNames.driveparkAccess],
    },
  },
  {
    path: '/fleet/calendar/reservations/new',
    name: 'fleet-reservation-create',
    component: fleetReservationCreate,
    meta: {
      title: 'Nova rezervacija',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Nova rezervacija' },
    },
  },
  {
    path: '/fleet/calendar/reservations/:reservationId/edit',
    name: 'fleet-reservation-edit',
    component: fleetReservationCreate,
    meta: {
      title: 'Uredi rezervaciju',
      requiredPermissions: [permissionNames.driveparkAccess],
    },
  },
  {
    path: '/fleet/forms',
    name: 'fleet-forms',
    component: fleetRecordsList,
    meta: {
      title: 'Zapisnici o primopredaji vozila',
      requiredPermissions: [permissionNames.driveparkAccess],
    },
  },
  {
    path: '/fleet/forms/new',
    name: 'fleet-record-pickup-create',
    component: fleetRecordForm,
    meta: {
      title: 'Zapisnik o preuzimanju',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Zapisnik o preuzimanju' },
    },
  },
  {
    path: '/fleet/forms/terms',
    name: 'fleet-record-pickup-terms',
    component: fleetPickupRecordTerms,
    meta: {
      title: 'Opći uvjeti',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Opći uvjeti' },
    },
  },
  {
    path: '/fleet/forms/:recordId/pickup',
    name: 'fleet-record-pickup-form',
    component: fleetRecordForm,
    meta: {
      title: 'Zapisnik o preuzimanju',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Zapisnik o preuzimanju' },
    },
  },
  {
    path: '/fleet/forms/:recordId/return',
    name: 'fleet-record-return-form',
    component: fleetRecordForm,
    meta: {
      title: 'Zapisnik o vraćanju',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Zapisnik o vraćanju' },
    },
  },
  {
    path: '/fleet/forms/:recordId/pickup/view',
    name: 'fleet-record-pickup-view',
    component: fleetRecordView,
    meta: {
      title: 'Zapisnik o preuzimanju',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Zapisnik o preuzimanju' },
    },
  },
  {
    path: '/fleet/forms/:recordId/return/view',
    name: 'fleet-record-return-view',
    component: fleetRecordView,
    meta: {
      title: 'Zapisnik o vraćanju',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Zapisnik o vraćanju' },
    },
  },
  {
    path: '/inventory/vehicles',
    name: 'inventory-vehicles',
    component: inventoryVehicles,
    meta: {
      title: 'Popis vozila',
      requiredPermissions: [permissionNames.driveparkAccess],
    },
  },
  {
    path: '/inventory/vehicles/new',
    name: 'inventory-vehicle-create',
    component: inventoryVehicleManage,
    meta: {
      title: 'Unesi vozilo',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Unesi vozilo' },
    },
  },
  {
    path: '/inventory/vehicles/:vehicleId/edit',
    name: 'inventory-vehicle-edit',
    component: inventoryVehicleManage,
    meta: {
      title: 'Uredi vozilo',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Uredi vozilo' },
    },
  },
  {
    path: '/inventory/vehicles/:vehicleId',
    name: 'inventory-vehicle-detail',
    component: inventoryVehicleDetail,
    meta: {
      title: 'Detalji vozila',
      requiredPermissions: [permissionNames.driveparkAccess],
      breadcrumb: { label: 'Detalji vozila' },
    },
  },
  {
    path: '/events/new',
    name: 'event-create',
    component: eventCreate,
    meta: {
      title: 'Kreiraj novi događaj',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsEdit,
      ],
      breadcrumb: {
        label: 'Novi događaj',
      },
    },
  },
  {
    path: '/events/:eventId/edit',
    name: 'event-edit',
    component: eventEdit,
    meta: {
      title: 'Uredi događaj',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsEdit,
      ],
      breadcrumb: {
        label: 'Uredi događaj',
      },
    },
  },
  {
    path: '/events/:eventId/invitees/new',
    name: 'event-invitee-create',
    component: eventManageInvitee,
    meta: {
      title: 'Dodaj uzvanika',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsInviteesAccess,
        permissionNames.eventsInviteesEdit,
      ],
      breadcrumb: {
        showEventName: true,
        label: 'Dodaj uzvanika',
      },
    },
  },
  {
    path: '/events/:eventId/invitees/:inviteeId/documents/:documentId/sign',
    name: 'event-invitee-document-sign',
    component: eventInviteeDocumentSign,
    meta: {
      bodyOnly: true,
      title: 'Potpis dokumenta',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsInviteesAccess,
        permissionNames.eventsDocumentsSign,
      ],
      breadcrumb: {
        showEventName: true,
        label: 'Potpis dokumenta',
      },
    },
  },
  {
    path: '/events/:eventId/invitees/:inviteeId/activities/:activityTypeId',
    name: 'event-invitee-activity-track',
    component: eventInviteeActivityTracking,
    meta: {
      bodyOnly: true,
      title: 'Detalji aktivnosti',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsInviteesAccess,
        permissionNames.eventsActivityStart,
      ],
      breadcrumb: {
        showEventName: true,
        label: 'Detalji aktivnosti',
      },
    },
  },
  {
    path: '/events/:eventId/invitees/:inviteeId',
    name: 'event-invitee-detail',
    component: eventInviteeDetail,
    meta: {
      title: 'Detalji uzvanika',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsInviteesAccess,
      ],
      breadcrumb: {
        showEventName: true,
        showInviteeName: true,
      },
    },
  },
  {
    path: '/events/:eventId/invitees/:inviteeId/edit',
    name: 'event-invitee-edit',
    component: eventManageInvitee,
    meta: {
      title: 'Uredi uzvanika',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsInviteesAccess,
        permissionNames.eventsInviteesEdit,
      ],
      breadcrumb: {
        showEventName: true,
        label: 'Uredi uzvanika',
      },
    },
  },
  {
    path: '/events/:eventId/invitees',
    name: 'event-detail-invitees',
    component: eventInvitees,
    meta: {
      title: 'Popis uzvanika',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsInviteesAccess,
      ],
      breadcrumb: {
        showEventName: true,
      },
    },
  },
  {
    path: '/events/:eventId/vehicles/select',
    name: 'event-vehicles-select',
    component: eventVehicleSelection,
    meta: {
      title: 'Odaberi vozila',
      requiredPermissions: [
        permissionNames.eventsAccess,
        permissionNames.eventsEdit,
      ],
      breadcrumb: {
        showEventName: true,
        label: 'Odaberi vozila',
      },
    },
  },
  {
    path: '/events/:eventId/vehicles',
    name: 'event-detail-vehicles',
    component: eventVehicles,
    meta: {
      title: 'Popis vozila',
      requiredPermissions: [permissionNames.eventsAccess],
      breadcrumb: {
        showEventName: true,
        label: 'Vozila',
      },
    },
  },
  {
    path: '/events/:eventId',
    name: 'event-detail',
    component: eventDetail,
    meta: {
      title: 'Detalji događaja',
      requiredPermissions: [permissionNames.eventsAccess],
      breadcrumb: {
        showEventName: true,
      },
    },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

registerAuthGuard(router)

export default router
